import React, { useEffect, useState, useRef } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createCategory,
  updateCategory,
  deleteCategory,
  getCategory
} from '../api/categoryAPI';
import { fetchData } from '../utils/apiHelpers';

const CategoryListLayer = () => {
  const userProfile = useUserProfile();
  const [categoryList, setCategoryList] = useState([]);
  const [newCategory, setNewCategory] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchCategories = () => {
    fetchData(getCategory, {}, setCategoryList);
  };


  const validateForm = () => {
    const newErrors = {};
    if (!newCategory.categoryName?.trim()) newErrors.categoryName = 'Category name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (categoryList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [categoryList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewCategory((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    setNewCategory(item);
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await deleteCategory(id);
      // repaint the table
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchCategories();
    } catch (error) {
      console.error('Error deleting category:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const cleanPayload = {};
      for (const key in newCategory) {
        if (newCategory[key] !== null && typeof newCategory[key] !== 'object') {
          cleanPayload[key] = newCategory[key];
        }
      }

      if (editingId) {
        await updateCategory(editingId, cleanPayload);
      } else {
        await createCategory(cleanPayload);
      }

      setShowModal(false);
      setEditingId(null);

      // repaint the table
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      setNewCategory({});
      await fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('categories:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewCategory({});
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Category
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Category Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categoryList.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.categoryName}</strong></td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('categories:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('categories:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Category' : 'New Category'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">Category Name *</label>
                    <input
                      className={`form-control ${errors.categoryName ? 'is-invalid' : ''}`}
                      name="categoryName"
                      value={newCategory.categoryName || ''}
                      onChange={handleInputChange}
                    />
                    {errors.categoryName && <div className="invalid-feedback">{errors.categoryName}</div>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                <button className="btn btn-primary" onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryListLayer;
